import type { Rank, RankLevel } from "@rivaled/shared";

export type { Rank, RankLevel };

export type RankIconProps = {
  rank: Rank;
  level?: RankLevel;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  showLevels?: boolean;
  showLabel?: boolean;
  levelLayout?: "bottom" | "right";
  showTooltip?: boolean;
  className?: string;
};

export type RankColors = {
  name: string;
  text: string;
  border: string;
  glow: string;
  glowStrong: string;
  glowClass: string;
};

/** Returns `var(--color-rank-<rank>)` for use in inline styles. */
export function rankVar(rank: Rank): string {
  return `var(--color-rank-${rank})`;
}

/** Returns a `color-mix()` expression for a rank color at the given opacity (0–100%). */
export function rankVarAlpha(rank: Rank, percent: number): string {
  return `color-mix(in srgb, var(--color-rank-${rank}) ${percent}%, transparent)`;
}

// Tailwind classes use rank-* color tokens defined in index.css @theme.
// For inline styles, use rankVar() and rankVarAlpha() instead of hex strings.
export const rankColors: Record<Rank, RankColors> = {
  unranked: {
    name: "Unranked",
    text: "text-rank-unranked",
    border: "border-rank-unranked/50",
    glow: "bg-rank-unranked/20",
    glowStrong: "bg-rank-unranked/40",
    glowClass: "",
  },
  bronze: {
    name: "Bronze",
    text: "text-rank-bronze",
    border: "border-rank-bronze/50",
    glow: "bg-rank-bronze/20",
    glowStrong: "bg-rank-bronze/40",
    glowClass: "glow-bronze",
  },
  silver: {
    name: "Silver",
    text: "text-rank-silver",
    border: "border-rank-silver/50",
    glow: "bg-rank-silver/10",
    glowStrong: "bg-rank-silver/25",
    glowClass: "glow-silver",
  },
  gold: {
    name: "Gold",
    text: "text-rank-gold",
    border: "border-rank-gold/50",
    glow: "bg-rank-gold/10",
    glowStrong: "bg-rank-gold/25",
    glowClass: "glow-gold",
  },
  platinum: {
    name: "Platinum",
    text: "text-rank-platinum",
    border: "border-rank-platinum/50",
    glow: "bg-rank-platinum/12",
    glowStrong: "bg-rank-platinum/28",
    glowClass: "glow-platinum",
  },
  diamond: {
    name: "Diamond",
    text: "text-rank-diamond",
    border: "border-rank-diamond/50",
    glow: "bg-rank-diamond/15",
    glowStrong: "bg-rank-diamond/30",
    glowClass: "glow-diamond",
  },
  prime: {
    name: "Prime",
    text: "text-rank-prime",
    border: "border-rank-prime/50",
    glow: "bg-rank-prime/25",
    glowStrong: "bg-rank-prime/45",
    glowClass: "glow-prime",
  },
  champion: {
    name: "Champion",
    text: "text-rank-champion",
    border: "border-rank-champion/50",
    glow: "bg-rank-champion/35",
    glowStrong: "bg-rank-champion/55",
    glowClass: "glow-champion",
  },
};

/** Resolve `--color-rank-<rank>` CSS variable to a hex string at runtime. */
export function rankHex(rank: Rank): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--color-rank-${rank}`).trim();
}

export const rankImages: Record<Rank, string> = {
  unranked: "/rank_icons/rank_unranked.png",
  bronze: "/rank_icons/rank_bronze.png",
  silver: "/rank_icons/rank_silver.png",
  gold: "/rank_icons/rank_gold.png",
  platinum: "/rank_icons/rank_platinum.png",
  diamond: "/rank_icons/rank_diamond.png",
  prime: "/rank_icons/rank_prime.png",
  champion: "/rank_icons/rank_champion.png",
};

const levelImages: Partial<Record<Rank, string>> = {
  bronze: "/rank_icons/rank_bronze_level.png",
  silver: "/rank_icons/rank_silver_level.png",
  gold: "/rank_icons/rank_gold_level.png",
  platinum: "/rank_icons/rank_platinum_level.png",
  diamond: "/rank_icons/rank_diamond_level.png",
};

const placeholderLevel = "/rank_icons/placeholder_level.png";

export const levelNumerals = ["", "I", "II", "III", "IV"];

const sizes = {
  xs: { container: "w-6", icon: "w-full", level: "w-[18%]", levelRight: "w-1.5 h-1.5" },
  sm: { container: "w-8", icon: "w-full", level: "w-[18%]", levelRight: "w-2 h-2" },
  md: { container: "w-12", icon: "w-full", level: "w-[18%]", levelRight: "w-2.5 h-2.5" },
  lg: { container: "w-16", icon: "w-full", level: "w-[18%]", levelRight: "w-3 h-3" },
  xl: { container: "w-20", icon: "w-full", level: "w-[18%]", levelRight: "w-4 h-4" },
  xxl: { container: "w-32", icon: "w-full", level: "w-[13%]", levelRight: "w-5 h-5" },
};

const maxLevels: Record<Rank, number> = {
  unranked: 0,
  bronze: 4,
  silver: 4,
  gold: 4,
  platinum: 4,
  diamond: 4,
  prime: 0,
  champion: 0,
};

const labelSizes: Record<string, string> = {
  xs: "text-2xs",
  sm: "text-2xs",
  md: "text-xs",
  lg: "text-sm",
  xl: "text-base",
  xxl: "text-lg",
};

export function RankIcon({
  rank,
  level = 0,
  size = "md",
  showLevels = true,
  showLabel = false,
  levelLayout = "bottom",
  showTooltip = false,
  className = "",
}: RankIconProps) {
  const totalLevels = maxLevels[rank];
  const hasLevels = showLevels && totalLevels > 0;
  const levelImage = levelImages[rank];
  const sizeConfig = sizes[size];
  const isRight = levelLayout === "right";

  const tooltipText =
    totalLevels > 0 ? `${rankColors[rank].name} ${levelNumerals[level]}` : rankColors[rank].name;

  const labelText =
    totalLevels > 0 && levelNumerals[level]
      ? `${rankColors[rank].name} ${levelNumerals[level]}`
      : rankColors[rank].name;

  const levelPips = hasLevels && (
    <div
      className={`flex ${isRight ? "flex-col-reverse" : "flex-row -mt-2"} justify-center gap-0.5 ${isRight ? "" : "w-full"}`}
    >
      {Array.from({ length: totalLevels }).map((_, i) => (
        <img
          key={i}
          src={i < level ? levelImage : placeholderLevel}
          alt={i < level ? `Level ${i + 1}` : "Unachieved level"}
          className={`${isRight ? sizeConfig.levelRight : sizeConfig.level} aspect-square object-contain`}
        />
      ))}
    </div>
  );

  return (
    <div
      className={`${showLabel ? "" : isRight ? "" : sizeConfig.container} flex ${isRight && !showLabel ? "flex-row items-center" : showLabel ? "flex-row items-center" : "flex-col items-center"} gap-1 relative group ${className}`}
    >
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-surface-800 text-white text-xs font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          {tooltipText}
        </div>
      )}
      {showLabel ? (
        <>
          <div className={`${sizeConfig.container} shrink-0 flex flex-col items-center gap-1`}>
            <img
              src={rankImages[rank]}
              alt={`${rank} rank`}
              className={`${sizeConfig.icon} aspect-square object-contain`}
            />
            {levelPips}
          </div>
          <span
            className={`${labelSizes[size]} font-display font-bold uppercase tracking-widest whitespace-nowrap`}
            style={{ color: `var(--color-rank-${rank})` }}
          >
            {labelText}
          </span>
        </>
      ) : (
        <>
          <div className={`${sizeConfig.container} shrink-0`}>
            <img
              src={rankImages[rank]}
              alt={`${rank} rank`}
              className={`${sizeConfig.icon} aspect-square object-contain`}
            />
          </div>
          {levelPips}
        </>
      )}
    </div>
  );
}
